import axios from "axios";
import { useEffect, useRef, useState } from "react";
import ChromeTitle from "../Home/ChromeTitle";
import "./Contact.css";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Field = ({ id, label, required, error, textarea, ...inputProps }) => (
  <div className={`contact-field${error ? " contact-field--error" : ""}`}>
    <label htmlFor={id}>
      {label}
      {required && <span className="contact-required">*</span>}
    </label>
    {textarea ? (
      <textarea id={id} rows={5} aria-invalid={error} {...inputProps} />
    ) : (
      <input id={id} aria-invalid={error} {...inputProps} />
    )}
  </div>
);

const Contact = (props) => {
  const { setModal } = props;
  const dialogRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const [errorFields, setErrorFields] = useState({
    firstName: false,
    lastName: false,
    email: false,
    message: false,
  });
  const [contactObj, setContactObj] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    company: "",
    message: "",
  });

  // Escape closes the form, and the page behind it doesn't scroll while open.
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") setModal(false);
    };
    document.addEventListener("keydown", handleKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = overflow;
    };
  }, [setModal]);

  const handleChangeField = (e) => {
    const { id, value } = e.target;
    setErrorFields({ ...errorFields, [id]: false });
    setContactObj({ ...contactObj, [id]: value });
  };

  const missingRequired =
    !contactObj.firstName.trim() ||
    !contactObj.lastName.trim() ||
    !contactObj.email.trim() ||
    !contactObj.message.trim();

  const handleSend = async (e) => {
    e.preventDefault();
    //validate data first
    const errors = {
      firstName: !contactObj.firstName.trim(),
      lastName: !contactObj.lastName.trim(),
      email: !EMAIL_PATTERN.test(contactObj.email.trim()),
      message: !contactObj.message.trim(),
    };
    setErrorFields(errors);
    if (Object.values(errors).some(Boolean)) return;

    setSending(true);
    setSendFailed(false);
    try {
      const { data } = await axios.post(
        "https://tower.paperairmedia.com/api/contactUs",
        {
          firstName: contactObj.firstName,
          lastName: contactObj.lastName,
          email: contactObj.email,
          phone: contactObj.phone,
          message: contactObj.message,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (data && data.message === "Sent") {
        setSuccess(true);
      } else {
        setSendFailed(true);
      }
    } catch (err) {
      setSendFailed(true);
    }
    setSending(false);
  };

  const hasFieldErrors = Object.values(errorFields).some(Boolean);

  return (
    <div
      className="contact-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setModal(false);
      }}
    >
      <div
        className="contact-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <button
          type="button"
          className="contact-close"
          aria-label="Close"
          onClick={() => setModal(false)}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <h2 id="contact-title" className="contact-title">
          <ChromeTitle text="Contact Us" />
        </h2>
        <p className="contact-intro">
          Please fill out the form below to inquire about a collaboration, or
          simply say hello.
        </p>

        {success ? (
          <>
            <p className="contact-success">
              Thank you! Your message has been sent to the PaperAirplane team.
            </p>
            <div className="contact-actions contact-actions--end">
              <button
                type="button"
                className="contact-btn contact-btn--primary"
                onClick={() => setModal(false)}
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSend} noValidate>
            <div className="contact-row">
              <Field
                id="firstName"
                label="First Name"
                required
                error={errorFields.firstName}
                value={contactObj.firstName}
                placeholder="First Name"
                autoComplete="given-name"
                onChange={handleChangeField}
              />
              <Field
                id="lastName"
                label="Last Name"
                required
                error={errorFields.lastName}
                value={contactObj.lastName}
                placeholder="Last Name"
                autoComplete="family-name"
                onChange={handleChangeField}
              />
            </div>
            <div className="contact-row">
              <Field
                id="phone"
                label="Phone"
                type="tel"
                value={contactObj.phone}
                placeholder="Phone"
                autoComplete="tel"
                onChange={handleChangeField}
              />
              <Field
                id="email"
                label="Email"
                type="email"
                required
                error={errorFields.email}
                value={contactObj.email}
                placeholder="Email"
                autoComplete="email"
                onChange={handleChangeField}
              />
            </div>
            <Field
              id="message"
              label="Message"
              textarea
              required
              error={errorFields.message}
              value={contactObj.message}
              placeholder="Enter your message..."
              onChange={handleChangeField}
            />

            {hasFieldErrors && (
              <p className="contact-error" role="alert">
                {missingRequired
                  ? "Please fill out all required fields."
                  : "Please enter a valid email address."}
              </p>
            )}
            {sendFailed && (
              <p className="contact-error" role="alert">
                Sorry, your message could not be sent. Please try again.
              </p>
            )}

            <div className="contact-actions">
              <button
                type="button"
                className="contact-btn contact-btn--secondary"
                onClick={() => setModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="contact-btn contact-btn--primary"
                disabled={sending}
              >
                {sending ? "Sending..." : "Send"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
export default Contact;
